'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import gsap from 'gsap';
import { useRouter } from 'next/navigation';

type TransitionContextValue = {
  navigate: (href: string) => void;
};

const PageTransitionContext = createContext<TransitionContextValue | null>(
  null
);

export function usePageTransition() {
  return useContext(PageTransitionContext);
}

type PageTransitionProps = {
  children: React.ReactNode;
  colors?: string;
};

const PageTransition = ({ children, colors }: PageTransitionProps) => {
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(true);
  const bannerColorsRef = useRef<string[]>([]);

  const bannerIds = [
    'banner-1',
    'banner-2',
    'banner-3',
    'banner-4',
    'banner-5',
    'banner-6',
  ];

  useEffect(() => {
    bannerColorsRef.current = bannerIds.map(() => colors || '#fff');
  }, [colors]);

  useEffect(() => {
    const animateIn = () => {
      const banners = document.querySelectorAll('[id^="banner-"]');
      if (banners.length === 0) {
        console.warn('PageTransition: No banners found');
        return;
      }

      const bannersArray = gsap.utils.toArray(banners);
      gsap.killTweensOf(bannersArray);
      gsap.set(bannersArray, { yPercent: 0 });

      gsap.to(bannersArray, {
        yPercent: 100,
        stagger: 0.2,
        delay: 0.5,
        duration: 0.8,
        ease: 'power2.inOut',
        onComplete: () => setIsVisible(false),
      });
    };

    if (typeof window !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', animateIn);
        return () =>
          document.removeEventListener('DOMContentLoaded', animateIn);
      } else {
        requestAnimationFrame(animateIn);
      }
    }
  }, []);

  const animatePageOut = (href: string) => {
    if (typeof window !== 'undefined') {
      setIsVisible(true);

      requestAnimationFrame(() => {
        const banners = document.querySelectorAll('[id^="banner-"]');
        if (banners.length === 0) {
          console.warn('PageTransition: No banners found, navigating directly');
          router.push(href);
          return;
        }

        const bannersArray = gsap.utils.toArray(banners);
        gsap.killTweensOf(bannersArray);
        gsap.set(bannersArray, { yPercent: -100 });

        gsap.to(bannersArray, {
          yPercent: 0,
          stagger: 0.2,
          duration: 0.8,
          ease: 'power2.inOut',
          onComplete: () => {
            router.push(href);
          },
        });
      });
    }
  };

  const navigate = (href: string) => {
    animatePageOut(href);
  };

  return (
    <PageTransitionContext.Provider value={{ navigate }}>
      {children}
      {isVisible && (
        <div
          style={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'row',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          {bannerIds.map((id, index) => (
            <div
              key={id}
              id={id}
              style={{
                flex: '1 1 auto',
                backgroundColor: bannerColorsRef.current[index] || '#fff',
                display: 'flex',
                flexDirection: 'column',
              }}
            />
          ))}
        </div>
      )}
    </PageTransitionContext.Provider>
  );
};
export default PageTransition;
